import { openNotification, makeId, isValidURL } from 'util/helpers';
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter';
import { Document, NodeIO, WebIO } from '@gltf-transform/core';
import { mergeDocuments, unpartition, prune } from '@gltf-transform/functions';
import { ALL_EXTENSIONS } from '@gltf-transform/extensions';
import * as THREE from 'three';
import * as CryptoJS from 'crypto-js';
import { AES_KEY } from 'util/constants';

export default async function exportScene({
  isSignIn,
  saveNewTemplate,
  setExportSceneProgress,
  setIsExportScene,
  isExportScene,
  setCancelExportRef,
  scene,
  cancelExportRef,
  email,
  getUnityPackage,
  selectableAssets,
  sceneAssets,
}) {
  if (!isSignIn) {
    saveNewTemplate();
    return;
  }
  let uploadInterval;

  function cleanup() {
    if (uploadInterval) {
      clearInterval(uploadInterval);
    }
    setExportSceneProgress(0);
    setIsExportScene(false);
    openNotification('Info', 'Canceled export process', 'bottomRight', 10);
    setCancelExportRef(false);
  }

  const exportError =
    'Seems like we have encountered an issue during the export. If the behavior repeats please reach out to us.';
  const uploadGLB = async (file: File, creator: string, type: string) => {
    const data = new FormData();
    data.set('file', file);
    data.set('fileName', file.name);
    data.set('email', creator);
    data.set('fileType', type);

    const res = await fetch('/api/upload-file-s3', {
      method: 'POST',
      body: data,
    }).then((res) => res.json());

    if (!res?.status) throw new Error('Error when uploading image');

    const assetURL = `${creator}/${type}/${file.name}`;

    return assetURL;
  };

  function getAnimations(scene) {
    const animations = [] as any;

    scene.traverse(function (object) {
      animations.push(...object.animations);
    });

    return animations;
  }

  setExportSceneProgress(0);

  openNotification('info', 'Scene export started 🚀', 'bottomRight', 10);

  let interval = setInterval(() => {
    setExportSceneProgress((prev) => {
      if (prev >= 29) {
        clearInterval(interval);
      }
      return prev + Math.floor(Math.random() * 3);
    });
  }, 3300);

  const animations = getAnimations(scene);

  const exporter = new GLTFExporter();
  openNotification(
    "Let's start!",
    'Preparing scene assets...',
    'bottomRight',
    10
  );

  const exportSceneGltf = () => {
    return new Promise<File>(async (resolve, reject) => {
      //export using gltf-transform
      try {
        let draco3d;
        if (typeof window === 'undefined') {
          draco3d = await import('draco3dgltf');
        }
        const io = new WebIO()
          .registerExtensions(ALL_EXTENSIONS)
          .registerDependencies({
            'draco3d.decoder': await draco3d.createDecoderModule(), // Optional.
            'draco3d.encoder': await draco3d.createEncoderModule(), // Optional.
          });

        const mainDocument = new Document();
        const assetsToExport = selectableAssets.filter((asset) =>
          isValidURL(asset.props.asset.url)
        );

        for (const asset of assetsToExport) {
          const document = await io.read(asset.props.asset.url);
          const root = document.getRoot();
          const mainNode = root.listNodes()[0];
          const threeObj = asset.props.asset.ref.current;

          const position = threeObj.position;
          const scale = threeObj.scale;
          const rotation = threeObj.rotation;
          const quat = new THREE.Quaternion().setFromEuler(rotation);

          mainNode.setTranslation([position.x, position.y, position.z]);
          mainNode.setRotation([quat.x, quat.y, quat.z, quat.w]);
          mainNode.setScale([scale.x, scale.y, scale.z]);

          mergeDocuments(mainDocument, document);
        }

        const sceneAssetsToExport = sceneAssets.filter((asset) =>
          isValidURL(asset.props.asset.url)
        );

        for (const asset of sceneAssetsToExport) {
          const document = await io.read(asset.props.asset.url);
          mergeDocuments(mainDocument, document);
        }

        const root = mainDocument.getRoot();
        const mainScene = root.listScenes()[0];

        for (const scene of root.listScenes()) {
          if (scene === mainScene) continue;

          for (const child of scene.listChildren()) {
            mainScene.addChild(child);
          }

          scene.dispose();
        }

        await mainDocument.transform(prune());
        await mainDocument.transform(unpartition());

        const glb = await io.writeBinary(mainDocument);

        let blob = new Blob([glb], { type: 'application/octet-stream' });
        let file = new File([blob], `scene-${makeId(10)}.glb`);

        /* const assetURL = await uploadGLB(file, email, 'assets'); */

        resolve(file);
      } catch (e) {
        openNotification('Error', exportError, 'bottomRight', 10);
        setIsExportScene(() => false);
        reject(e);
      }
    });
  };

  const saveAsUnityPackage = async (file) => {
    if (cancelExportRef.current) {
      cleanup();
      return;
    }
    const assetURL = await uploadGLB(file, email, 'assets');

    if (cancelExportRef.current) {
      cleanup();
      return;
    }

    openNotification(
      'Info',
      'Packaging Unity goodies, this may take some time. Please get a cup of coffee while we work on it ☕️',
      'bottomRight',
      10
    );

    uploadInterval = setInterval(() => {
      setExportSceneProgress((prev) => {
        if (prev >= 69) {
          clearInterval(uploadInterval);
        }
        return prev + Math.floor(Math.random() * 3);
      });
    }, 3000);

    if (cancelExportRef.current) {
      cleanup();
      return;
    }

    const response = await getUnityPackage({
      variables: {
        data: {
          fileName: file.name,
          fileURL: assetURL,
        },
      },
    });

    if (cancelExportRef.current) {
      cleanup();
      return;
    }

    setExportSceneProgress(70);

    if (response.data?.getUnityPackage.status) {
      const fileURL = response.data.getUnityPackage.fileURL;

      const fileName =
        `${file.name.substring(0, file.name.lastIndexOf('.'))}.unitypackage` ||
        'unknown.unitypackage';

      fetch(fileURL)
        .then((res) => {
          if (cancelExportRef.current) {
            cleanup();
            return;
          }
          const contentLength: any = res.headers.get('Content-Length');
          const reader = res.body!.getReader();
          let receivedLength: number = 0;

          let chunk: any = [];
          function read() {
            reader
              .read()
              .then(({ done, value }) => {
                if (cancelExportRef.current) {
                  cleanup();
                  return;
                }
                if (done) {
                  let blob = new Blob([chunk], {
                    type: 'application/octet-stream',
                  });
                  let link = document.createElement('a');
                  link.href = URL.createObjectURL(blob);
                  link.download = fileName;
                  link.style.display = 'none';
                  document.body.appendChild(link);
                  link.click();
                  URL.revokeObjectURL(link.href);
                  document.body.removeChild(link);

                  setIsExportScene(() => false);
                  openNotification(
                    'Success',
                    'Great work! The export to Unity is complete. 🎉',
                    'bottomRight',
                    10
                  );

                  return;
                }

                receivedLength += value.length;

                const newChunk = new Uint8Array(chunk.length + value.length);
                newChunk.set(chunk);
                newChunk.set(value, chunk.length);

                chunk = newChunk;

                setExportSceneProgress((value) => {
                  let updatedValue = 70 + (receivedLength / contentLength) * 30;
                  return updatedValue.toFixed(2);
                });

                read();
              })
              .catch((e) => {
                console.log(e);
                openNotification('Error', exportError, 'bottomRight', 10);
              });
          }
          // start reading the stream
          read();
        })
        .catch((e) => {
          console.log(e);
          openNotification('Error', exportError, 'bottomRight', 10);
        });
    } else {
      openNotification('Error', exportError, 'bottomRight', 10);
    }
  };

  const saveAsGLB = async (file: File) => {
    const link = document.createElement('a');
    link.href = URL.createObjectURL(file);
    const fileName = file.name;
    link.download = fileName;
    link.textContent = 'Download package';
    document.body.appendChild(link);

    link.click();
    URL.revokeObjectURL(link.href);
    document.body.removeChild(link);

    setIsExportScene(() => false);
    openNotification(
      'Success',
      'Great work! The export to glb is complete. 🎉',
      'bottomRight',
      10
    );
  };

  const saveAsOzonePackage = async (file: File) => {
    const link = document.createElement('a');
    link.href = URL.createObjectURL(file);
    const fileName = `${file.name.replace(/\.[^/.]+$/, '')}.ozonepackage`;
    link.download = fileName;
    link.textContent = 'Download package';
    document.body.appendChild(link);

    link.click();
    URL.revokeObjectURL(link.href);
    document.body.removeChild(link);

    setIsExportScene(() => false);
    openNotification(
      'Success',
      'Great work! The export to Ozone package is complete. 🎉',
      'bottomRight',
      10
    );
  };

  const saveAsOzonePackageEncrypted = async (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      const key = CryptoJS.enc.Hex.parse(AES_KEY);
      const iv = CryptoJS.lib.WordArray.random(16);

      const wordArray = CryptoJS.lib.WordArray.create(reader.result);
      const encrypted = CryptoJS.AES.encrypt(wordArray, key, {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      });
      const encryptedBase64 = encrypted.toString();
      const ivBase64 = CryptoJS.enc.Base64.stringify(iv);

      const fileName = `${file.name.replace(/\.[^/.]+$/, '')}.zepetoweb`;
      const blob = new Blob([ivBase64.concat(encryptedBase64)], {
        type: 'application/octet-stream',
      });
      const newFile = new File([blob], fileName);

      const link = document.createElement('a');
      link.href = URL.createObjectURL(newFile);
      link.download = fileName;
      link.textContent = 'Download package';
      document.body.appendChild(link);

      link.click();
      URL.revokeObjectURL(link.href);
      document.body.removeChild(link);

      setIsExportScene(() => false);
      openNotification(
        'Success',
        'Great work! The export to Ozone package is complete. 🎉',
        'bottomRight',
        10
      );
    };
    reader.readAsArrayBuffer(file);
  };

  exportSceneGltf().then(async (file: File) => {
    switch (isExportScene) {
      case 'unity':
        await saveAsUnityPackage(file);
        break;
      case 'glb':
        await saveAsGLB(file);
        break;
      case 'ozonepackage':
        await saveAsOzonePackageEncrypted(file);
        break;
    }
  });
}
